// src/components/ClientDashboardLayout.jsx

import { useState, useEffect } from "react";
import { clearPortalSession } from '../../mobile/accountSession';
import { clearClientSession } from '../../auth/clientSession';
import { NotificationBell } from '../../mobile/NotificationCenter';
import { Link, Outlet, useLocation, useOutletContext, useNavigate } from "react-router-dom";
import {
  HomeIcon,
  FileTextIcon,
  FileSearchIcon,
  BookOpenIcon,
  BanknoteArrowUp,
  ScrollTextIcon,
  MenuIcon,
  XIcon,
  HandshakeIcon,
} from "lucide-react";
import {
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline'
const navItems = [
  { label: "Home", icon: <HomeIcon className="w-5 h-5" />, path: "/client/dashboard" },
  { label: "Credit Report", icon: <FileSearchIcon className="w-5 h-5" />, path: "/client/dashboard/oneview" },
  { label: "Creditors", icon: <BanknoteArrowUp className="w-5 h-5" />, path: "/client/dashboard/creditors" },
  { label: "Documents", icon: <FileTextIcon className="w-5 h-5" />, path: "/client/dashboard/documents" },
  { label: "Notifications", icon: <FileTextIcon className="w-5 h-5" />, path: "/client/dashboard/notifications" },
  { label: "FAQ & Resources", icon: <BookOpenIcon className="w-5 h-5" />, path: "/client/dashboard/faq" },
  { label: "Forms", icon: <ScrollTextIcon className="w-5 h-5" />, path: "/client/dashboard/forms" },
  { label: "Make Payment", icon: <CurrencyDollarIcon className="w-5 h-5" />, path: "/client/dashboard/payments" },
  { label: "Retain Attorney", icon: <HandshakeIcon className="w-5 h-5" />, path: "/client/dashboard/retainer" },
];

export default function ClientDashboardLayout() {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL;
  const [allSigned, setAllSigned] = useState(false);
  const [creditorsPulled, setCreditorsPulled] = useState(false);
  const [checklistItems, setChecklistItems] = useState(null);
  const [documentsComplete, setDocumentsComplete] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [preFilingFees, setPreFilingFees] = useState(0);
  const [feesPaidPrior, setFeesPaidPrior] = useState(0);
  const [retainerStatus, setRetainerStatus] = useState(false);
  const [caseSummary, setCaseSummary] = useState(null);
  const [stepStatus, setStepStatus] = useState({
       retainer: false,
       creditors: false,
       documents: false,
       filed: false
     });
  const navigate = useNavigate();
  const handleSignOut = async () => {
    try { await clearPortalSession(); navigate("/client/login", { replace: true }); }
    catch (error) { window.alert(error.message); }
  };
  const caseId = localStorage.getItem('clientCaseId');
  useEffect(() => {
    const token = localStorage.getItem("clientToken");
    if (!token || !caseId) {
      clearClientSession();
      navigate("/client/login", { replace: true });
      return;
    }

    fetch(`${API_URL}/case/${caseId}/checklist/`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => res.json())
      .then(data => {
        setChecklistItems(data);
        // Determine if every checklist item has at least one accepted upload
        const allAccepted = data.every(item =>
          Array.isArray(item.uploads) &&
          item.uploads.some(u => u.status === 'accepted')
        );
        setDocumentsComplete(allAccepted);
        setStepStatus(s => ({
          ...s,
          documents: allAccepted
        }));
      })
  
    let debtorCount = 1
    fetch(`${API_URL}/case/${caseId}/retainer-status/`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then(res => {
        if (res.status === 401 || res.status === 403) {
          clearClientSession();
          navigate("/client/login", { replace: true });
          throw new Error('Unauthorized');
        }
        return res.json();
      })
      .then(data => {
        // Treat all returned debtors as requiring signature
        const signedAll = data.length > 0 && data.every(d => d.signed);
        debtorCount = data.length === 1 ? 1 : 2;
        setAllSigned(signedAll);
        setRetainerStatus(data);
        setStepStatus(s => ({
          ...s,
          retainer: data.length > 0 && data.every(d => d.signed)
        }));
      })
      .catch(err => {
        if (err.message !== 'Unauthorized') {
          setAllSigned(false);
        }
      });
      fetch(`${API_URL}/case/${caseId}/oneview-download/`, {
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${localStorage.getItem("clientToken")}`,
        },
      })
        .then(res => res.json())
        .then(data => {
          if (data.length >= debtorCount) {
            setCreditorsPulled(true)
          }
          setStepStatus(s => ({
            ...s,
            creditors: data.length > 0
          }))
        });

      // Fetch tutorial‐seen to get fee information
      fetch(`${API_URL}/user/tutorial-seen/`, {
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${localStorage.getItem("clientToken")}`,
        },
      })
        .then(res => {
          if (!res.ok) throw new Error('Failed to fetch tutorial data');
          return res.json();
        })
        .then(data => {
          setPreFilingFees(data.pre_filing_fees || 0);
          setFeesPaidPrior(data.fees_paid_prior || 0);

          // Enhanced tutorial payload includes case status/chapter for banner purposes.
          setCaseSummary(data);

          const status = (data?.status || '').toLowerCase();
          const isFiledNow = status === 'filed' || status === 'confirmed' || data?.is_filed === true;

          if (isFiledNow) {
            setStepStatus(s => ({
              ...s,
              retainer: true,
              creditors: true,
              documents: true,
              filed: true,
            }));
          } else {
            setStepStatus(s => ({
              ...s,
              filed: false,
            }));
          }
        })
        .catch(() => {
          // In case of error, leave fees at zero and keep banner in pre-filing mode
          setCaseSummary(null);
        });

  }, [API_URL, caseId, navigate]);

  const caseStatus = (caseSummary?.status || '').toLowerCase();
  const isFiled = caseStatus === 'filed' || caseStatus === 'confirmed' || caseSummary?.is_filed === true;
  const chapter = String(caseSummary?.chapter || '').trim();

  // Filing fee (Chapter 7 only): hide step if already fully paid
  const filingFeeAmount = Number(caseSummary?.filing_fee_amount ?? 0);
  const filingFeesPaid = Number(caseSummary?.filing_fees_paid ?? 0);
  const filingFeeIsPaid = filingFeeAmount > 0 && filingFeesPaid >= filingFeeAmount;

  // Financial management course: consider complete if filed OR if a non-rejected upload exists on the checklist item
  const fmFiled = caseSummary?.financial_management_course_filed === true;
  const fmChecklistItem = Array.isArray(checklistItems)
    ? checklistItems.find(i => (i?.name || '').toLowerCase() === 'certificate of financial management'.toLowerCase())
    : null;
  const fmHasNonRejectedUpload = !!(
    fmChecklistItem &&
    Array.isArray(fmChecklistItem.uploads) &&
    fmChecklistItem.uploads.some(u => (u?.status || '') !== 'rejected')
  );
  const fmComplete = fmFiled || fmHasNonRejectedUpload;

  // Reaffirmations: only show step if there are any reaffirmation items
  const reaffirmations = caseSummary?.reaffirmations;
  const hasReaffirmations = Array.isArray(reaffirmations) && reaffirmations.length > 0;

  return (
    <div className="min-h-screen bg-gray-100 relative">
      {/* Fixed Top Progress Bar */}
      <div className="fixed top-0 left-0 right-0 z-40 bg-indigo-700 text-white py-3 px-4 text-sm shadow">
        <div className="text-center font-semibold">
          {isFiled
            ? '✅ Complete Post‑Filing Requirements'
            : (!allSigned
                ? '📌 Step 1 of 4: Retain Your Attorney'
                : !creditorsPulled
                ? '📌 Step 2 of 4: Get your Creditors'
                : !documentsComplete
                ? '📌 Step 3 of 4: Upload your Documents'
                : '📌 Step 4 of 4: File your Case')}
        </div>
        <p className="text-xs text-indigo-100 text-center mt-1 px-4">
          {isFiled ? (
            (() => {
              const parts = [];

              if (chapter === '13') {
                if (!fmComplete) parts.push('take the second course');
                parts.push('attend the meeting of creditors');
                parts.push('start making plan payments');
              } else {
                if (!(filingFeeIsPaid || filingFeeAmount === 0)) parts.push('pay filing fee');
                if (!fmComplete) parts.push('take the second course');
                parts.push('attend the meeting of creditors');
                if (hasReaffirmations) parts.push('sign reaffirmation agreement(s)');
              }

              const label = chapter === '13' ? 'Chapter 13' : 'Chapter 7';
              return <>{label}: {parts.length ? parts.join(' • ') : 'you are all set for now'}</>;
            })()
          ) : (
            !allSigned ? (
              <>Make the down payment and{' '}
                <Link to="/client/dashboard/retainer" className="underline font-semibold">
                  sign the retainer
                </Link>
              </>
            ) : !creditorsPulled ? (
              <>Pull your{' '}
                <Link to="/client/dashboard/oneview" className="underline font-semibold">
                  credit report
                </Link>, review, and add{' '}
                <Link to="/client/dashboard/creditors" className="underline font-semibold">
                  creditors
                </Link></>
            ) : !documentsComplete ? (
              <>Upload your{' '}
                <Link to="/client/dashboard/documents" className="underline font-semibold">
                  documents
                </Link>{' '}
                and take your credit counseling course
              </>
            ) : (
              <>
                <button
                  onClick={() => setShowChatModal(true)}
                  className="underline font-semibold"
                >
                  Schedule
                </button>{' '}
                your in-person signing/filing appointment and{' '}
                <Link
                  to={`/client/dashboard/payments/${(preFilingFees - feesPaidPrior).toFixed(2)}`}
                  className="underline font-semibold"
                >
                  Pay
                </Link>{' '}
                the balance of your fees
              </>
            )
          )}
        </p>
      </div>

      {/* ✅ Fixed Mobile Menu Button OUTSIDE of flex */}
      <button
        className="fixed top-2 left-2 z-50 bg-white rounded-full p-2 shadow-md md:hidden"
        onClick={() => setMobileOpen(true)}
      >
        <MenuIcon className="w-6 h-6 text-indigo-700" />
      </button>

      {/* Sidebar + Main Content */}
      <div className="pt-16 flex">
        {/* Sidebar */}
        <aside
          className={`fixed top-16 left-0 z-40 w-64 h-full bg-white border-r shadow-md transform transition-transform duration-300
            ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}
        >
          <div className="flex items-center justify-between px-4 py-4 border-b">
            <h1 className="text-lg font-bold text-indigo-700">Client Portal</h1>
            <button onClick={() => setMobileOpen(false)} className="md:hidden">
              <XIcon className="w-6 h-6 text-gray-500" />
            </button>
          </div>
          <nav className="mt-4 space-y-1 px-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center px-3 py-2 rounded-md text-sm font-medium hover:bg-indigo-50 hover:text-indigo-700 ${
                  location.pathname === item.path ? 'bg-indigo-100 text-indigo-700' : 'text-gray-700'
                }`}
                onClick={() => setMobileOpen(false)}
              >
                {item.icon}
                <span className="ml-2">{item.label}</span>
              </Link>
            ))}
          </nav>
          <div className="mt-8 px-10">
            <NotificationBell />
            <button
              onClick={handleSignOut}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
            >
              Sign Out
            </button>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 ml-0 md:ml-64 p-4">
          <Outlet context={{ retainerStatus,stepStatus, caseId }} />
        </main>
      </div>
      {/* Modal for scheduling chat */}
      {showChatModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white w-11/12 md:w-3/4 lg:w-1/2 h-[80vh] rounded-lg shadow-lg relative">
            <button
              onClick={() => setShowChatModal(false)}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
            >
              ×
            </button>
            <iframe
              src="https://outlook.office.com/owa/calendar/CisowskiLawCaseFilingAppointment@NETORGFT5747048.onmicrosoft.com/bookings/"
              title="Schedule Chat"
              className="w-full h-full rounded-b-lg"
            />
          </div>
        </div>
      )}
    </div>
  );
}
